// ⌘K / Ctrl+K: jump to a page, or to a contact by address.
//
// Pages are matched here, on the words in nav.ts. Contacts are asked of the
// API — GET /contacts?search= — with the same rules as the Kontaktok page:
// the address's beginning, or `*` and any part of it. The request waits for
// the typing to stop, because every keystroke is a query on a table of
// millions.
//
// One dialog and one keyboard listener for the whole shell
// (`QuickSearchProvider`); the shell places as many triggers as it needs —
// the field at the top of the sidebar, the icon in a phone's top bar.

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { SearchIcon, UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { ApiError, fetchContacts } from "@/lib/api";
import type { Contact } from "@/lib/api";
import { describeError } from "@/components/emc/states";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { EXTRA_DESTINATIONS, visibleNav } from "@/components/emc/shell/nav";

const SEARCH_DELAY_MS = 300;
const CONTACT_LIMIT = 8;
const MIN_QUERY = 2;

function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

function isMac(): boolean {
  return typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.userAgent);
}

type ContactSearch =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "done"; items: Contact[] }
  | { state: "error"; message: string };

const OpenContext = createContext<(() => void) | null>(null);

/** The search field that opens the dialog: in the sidebar, or an icon. */
export function QuickSearchTrigger({ variant = "field", className }: { variant?: "field" | "icon"; className?: string }) {
  const open = useContext(OpenContext);
  const shortcut = isMac() ? "⌘K" : "Ctrl K";
  if (open === null) return null;
  if (variant === "icon") {
    return (
      <Button
        variant="ghost"
        size="icon-sm"
        className={className}
        aria-label="Keresés oldalra, kontaktra"
        onClick={open}
      >
        <SearchIcon />
      </Button>
    );
  }
  return (
    <button
      type="button"
      aria-label="Keresés oldalra, kontaktra"
      onClick={open}
      className={cn(
        "flex h-8 w-full cursor-pointer items-center gap-2 rounded-inner bg-fill-secondary pr-1.5 pl-2.5 text-left text-detail text-muted-foreground transition-colors hover:bg-fill",
        className,
      )}
    >
      <SearchIcon className="size-3.75 shrink-0" />
      <span className="flex-1 truncate">Keresés</span>
      <kbd className="rounded-tag bg-card px-1.5 font-sans text-label font-medium text-muted-foreground shadow-card">{shortcut}</kbd>
    </button>
  );
}

export function QuickSearchProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const isAdmin = useAuth().user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [contacts, setContacts] = useState<ContactSearch>({ state: "idle" });

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const trimmed = query.trim();

  useEffect(() => {
    if (!open || trimmed.length < MIN_QUERY) {
      setContacts({ state: "idle" });
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setContacts({ state: "loading" });
      fetchContacts({ search: trimmed, limit: CONTACT_LIMIT })
        .then((page) => {
          if (!cancelled) setContacts({ state: "done", items: page.items });
        })
        .catch((problem: unknown) => {
          if (cancelled) return;
          const message =
            problem instanceof ApiError || problem instanceof Error ? problem.message : "";
          setContacts({ state: "error", message: describeError(message).what });
        });
    }, SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, trimmed]);

  const pages = useMemo(() => {
    const all = [
      ...visibleNav(isAdmin).flatMap((group) =>
        group.items.filter((item) => !item.soon).map((item) => ({ ...item, group: group.label })),
      ),
      ...EXTRA_DESTINATIONS,
    ];
    const needle = fold(trimmed);
    if (needle === "") return all;
    return all.filter((item) =>
      fold(`${item.label} ${item.group} ${item.keywords ?? ""}`).includes(needle),
    );
  }, [trimmed, isAdmin]);

  function go(to: string) {
    setOpen(false);
    setQuery("");
    navigate(to);
  }

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setQuery("");
  }

  const openDialog = useMemo(() => () => setOpen(true), []);

  return (
    <OpenContext.Provider value={openDialog}>
      {children}
      <CommandDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Gyorskereső"
        description="Oldal neve, vagy egy kontakt címének eleje"
        commandProps={{ shouldFilter: false, loop: true }}
      >
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder="Oldal, vagy kontakt: cím eleje, *részlet"
        />
        <CommandList>
          <CommandEmpty>
            {trimmed.length < MIN_QUERY
              ? "Írj be egy oldalnevet vagy egy címet."
              : contacts.state === "loading"
                ? "Keresés…"
                : "Nincs ilyen oldal, és nincs ilyen kontakt."}
          </CommandEmpty>
          {pages.length > 0 && (
            <CommandGroup heading="Oldalak">
              {pages.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem key={item.to} value={`page:${item.to}`} onSelect={() => go(item.to)}>
                    <Icon />
                    <span className="flex-1">{item.label}</span>
                    <span className="text-label text-muted-foreground">{item.group}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
          {contacts.state === "done" && contacts.items.length > 0 && (
            <CommandGroup heading="Kontaktok">
              {contacts.items.map((contact) => (
                <CommandItem
                  key={contact.id}
                  value={`contact:${contact.id}`}
                  onSelect={() => go(`/kontaktok/${contact.id}`)}
                >
                  <UserIcon />
                  <span className="flex-1 truncate">{contact.email}</span>
                  <CommandShortcut>{contact.country}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {contacts.state === "error" && (
            <p role="alert" className="px-3 py-2 text-body text-danger">
              A kontakt-keresés most nem megy: {contacts.message}
            </p>
          )}
          {contacts.state === "loading" && pages.length > 0 && (
            <p className="px-3 py-2 text-body text-muted-foreground">Kontaktok keresése…</p>
          )}
        </CommandList>
      </CommandDialog>
    </OpenContext.Provider>
  );
}
